import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { useEditorStore } from "@/store/editorStore";
import { applyCollectionItemsConfig, type CollectionItemsConfig } from "./CollectionItemsPanel";

interface SkinComponent {
  id: string;
  type: string;
  name: string;
  position: {
    left?: number | string;
    right?: number | string;
    top?: number | string;
    bottom?: number | string;
  };
  size: {
    width: number | string;
    height: number | string;
    minWidth?: number | string;
    minHeight?: number | string;
    maxWidth?: number | string;
    maxHeight?: number | string;
    aspectRatio?: string;
  };
  style: Record<string, unknown>;
  margin?: { top: number; right: number; bottom: number; left: number };
  padding?: { top: number; right: number; bottom: number; left: number };
  content: Record<string, unknown>;
  visible: boolean;
  parentId?: string;
  zIndex: number;
  pointerEvents?: boolean;
  escToHide?: boolean;
  hideOnStart?: boolean;
}

interface CollectionListRendererProps {
  component: SkinComponent;
  selectedComponent: SkinComponent | null;
  isPreviewMode: boolean;
  onComponentClick: (component: SkinComponent, e: React.MouseEvent) => void;
  allComponents: SkinComponent[];
}

export const CollectionListRenderer: React.FC<CollectionListRendererProps> = ({
  component,
  selectedComponent,
  isPreviewMode,
  onComponentClick,
  allComponents,
}) => {
  const { project, hotspots } = useEditorStore();

  // Get the data source
  const dataSourceId = component.content?.dataSourceId;
  const [collectionData, setCollectionData] = useState<Array<Record<string, unknown>>>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Get child components of this collection list
  const childComponents = allComponents.filter((c) => c.parentId === component.id);

  // Resolve [table.field] text variables using the current collection item
  const resolveItemTextVariables = (text: string, item: Record<string, unknown>): string => {
    return text.replace(/\[\s*(hotspots|panoramas)\.(\w+)\s*\]/g, (_match, _table, field) => {
      const value = item[field as string];
      if (value == null) return "";
      return typeof value === "string" ? value : String(value);
    });
  };

  // Helper function to render child components for each collection item
  const renderChildComponentsForItem = (item: Record<string, unknown>, itemIndex: number) => {
    return childComponents.map((childComponent) => {
      // Create a unique key for this instance of the child component
      const instanceKey = `${childComponent.id}-item-${itemIndex}`;

      // Determine text content: data-binding takes priority, then resolve text variables
      const hasDataBinding =
        childComponent.content?.dataBinding &&
        childComponent.content.dataBinding !== "none" &&
        item[childComponent.content.dataBinding];

      let contentOverrides: Record<string, unknown> = {};
      if (hasDataBinding) {
        contentOverrides =
          childComponent.type === "image"
            ? { src: item[childComponent.content.dataBinding] }
            : { text: item[childComponent.content.dataBinding] };
      } else if (
        childComponent.type === "text" &&
        typeof childComponent.content?.text === "string"
      ) {
        contentOverrides = {
          text: resolveItemTextVariables(childComponent.content.text, item),
        };
      }

      // Clone the child component with bound data
      const boundComponent = {
        ...childComponent,
        id: instanceKey, // Use unique ID for this instance
        // Preserve all important properties
        visible: childComponent.visible,
        zIndex: childComponent.zIndex,
        pointerEvents: childComponent.pointerEvents,
        escToHide: childComponent.escToHide,
        style: childComponent.style,
        position: childComponent.position,
        size: childComponent.size,
        margin: childComponent.margin,
        padding: childComponent.padding,
        content: {
          ...childComponent.content,
          ...contentOverrides,
        },
      };

      return renderComponent(boundComponent);
    });
  };

  // Basic component renderer (simplified for collection list items)
  const renderComponent = (comp: SkinComponent) => {
    const baseStyle: React.CSSProperties = {
      position: "relative",
      left:
        typeof comp.position.left === "string"
          ? comp.position.left
          : `${comp.position.left || 0}px`,
      top:
        typeof comp.position.top === "string" ? comp.position.top : `${comp.position.top || 0}px`,
      width: typeof comp.size.width === "number" ? `${comp.size.width}px` : comp.size.width,
      height: typeof comp.size.height === "number" ? `${comp.size.height}px` : comp.size.height,
      minWidth:
        typeof comp.size.minWidth === "number" ? `${comp.size.minWidth}px` : comp.size.minWidth,
      minHeight:
        typeof comp.size.minHeight === "number" ? `${comp.size.minHeight}px` : comp.size.minHeight,
      maxWidth:
        typeof comp.size.maxWidth === "number" ? `${comp.size.maxWidth}px` : comp.size.maxWidth,
      maxHeight:
        typeof comp.size.maxHeight === "number" ? `${comp.size.maxHeight}px` : comp.size.maxHeight,
      aspectRatio: comp.size.aspectRatio,
      zIndex: comp.zIndex,
      pointerEvents: comp.pointerEvents ? "auto" : "none",
      ...comp.style,
    };

    switch (comp.type) {
      case "text":
        return (
          <button
            key={comp.id}
            type="button"
            style={baseStyle}
            onClick={(e) => {
              e.stopPropagation();
              onComponentClick(comp, e);
            }}
          >
            <span style={{ color: "#ffffff", fontSize: "14px" }}>
              {comp.content?.text || "Text"}
            </span>
          </button>
        );

      case "image":
        return (
          <button
            key={comp.id}
            type="button"
            style={baseStyle}
            onClick={(e) => {
              e.stopPropagation();
              onComponentClick(comp, e);
            }}
          >
            <img
              src={comp.content?.src || "/placeholder.svg"}
              alt={comp.content?.alt || "Image"}
              style={{
                width: "100%",
                height: "100%",
                objectFit: comp.content?.objectFit || "cover",
                // Apply border radius to match the container
                borderRadius: comp.style.borderRadius
                  ? `${comp.style.borderRadius}px`
                  : comp.style.borderTopLeftRadius ||
                      comp.style.borderTopRightRadius ||
                      comp.style.borderBottomLeftRadius ||
                      comp.style.borderBottomRightRadius
                    ? `${comp.style.borderTopLeftRadius || 0}px ${comp.style.borderTopRightRadius || 0}px ${comp.style.borderBottomRightRadius || 0}px ${comp.style.borderBottomLeftRadius || 0}px`
                    : "0px",
              }}
            />
          </button>
        );

      default:
        return (
          <button
            key={comp.id}
            type="button"
            style={baseStyle}
            onClick={(e) => {
              e.stopPropagation();
              onComponentClick(comp, e);
            }}
          >
            {comp.type} component
          </button>
        );
    }
  };

  const getItemKey = (item: Record<string, unknown>) => {
    const candidate =
      (item as { id?: string | number }).id ??
      (item as { _id?: string | number })._id ??
      (item as { slug?: string }).slug ??
      (item as { name?: string }).name;
    if (typeof candidate === "string" || typeof candidate === "number") {
      return String(candidate);
    }
    return JSON.stringify(item);
  };

  // Load collection data
  useEffect(() => {
    const loadCollectionData = async () => {
      if (!dataSourceId || !project?.craftspace_id) return;

      try {
        setIsLoading(true);
        console.log("🔄 Loading collection data...", {
          dataSourceId,
          craftspaceId: project.craftspace_id,
        });
        const { CMSService } = await import("@/services/cmsService");
        const datasetWithContent = await CMSService.loadCMSDataWithContent(
          dataSourceId,
          project.craftspace_id,
        );
        const rows = datasetWithContent.data?.rows || [];
        console.log("📊 Collection data loaded:", {
          rowsLength: rows.length,
          sampleData: rows.slice(0, 2),
        });
        setCollectionData(rows);
      } catch (error) {
        console.error("Error loading collection data:", error);
        setCollectionData([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadCollectionData();
  }, [dataSourceId, project?.craftspace_id]);

  // Apply filters and sort from collection items config
  const collectionItemsConfig = component.content?.collectionItemsConfig as CollectionItemsConfig | undefined;

  const processedData = useMemo(() => {
    if (!collectionItemsConfig) return collectionData;
    // Infer field types from actual data for type-aware sorting/filtering
    const fields: Array<{ name: string; fieldType?: string }> = collectionData.length > 0
      ? Object.keys(collectionData[0]).map((key) => {
          const sample = collectionData[0][key];
          let fieldType = "text";
          if (typeof sample === "number") fieldType = "number";
          else if (typeof sample === "boolean") fieldType = "switch";
          else if (typeof sample === "string" && !Number.isNaN(Date.parse(sample)) && sample.includes("-")) fieldType = "date";
          return { name: key, fieldType };
        })
      : [];
    return applyCollectionItemsConfig(collectionData, collectionItemsConfig, fields);
  }, [collectionData, collectionItemsConfig]);

  // Create a content hash to detect when hotspots change
  const hotspotContentHash = useMemo(() => {
    if (!hotspots || hotspots.length === 0) return "";
    return hotspots
      .map((h) => `${h.id}-${h.name}-${h.visible}-${JSON.stringify(h.content)}-${h.updated_at}`)
      .join("|");
  }, [hotspots]);

  // Refresh collection data when hotspots change (for hotspots collection)
  useEffect(() => {
    const refreshCollectionData = async () => {
      if (!dataSourceId || !project?.craftspace_id) return;

      // Only refresh if this is a hotspots collection
      if (!dataSourceId.includes("hotspots") && !dataSourceId.includes("Hotspots")) return;

      try {
        console.log("🔄 Hotspots changed, refreshing collection data...", {
          dataSourceId,
          hotspotContentHash: `${hotspotContentHash.substring(0, 50)}...`,
        });
        const { CMSService } = await import("@/services/cmsService");
        const datasetWithContent = await CMSService.loadCMSDataWithContent(
          dataSourceId,
          project.craftspace_id,
        );
        const newData = datasetWithContent.data?.rows || [];

        console.log("📊 New collection data loaded:", {
          newDataLength: newData.length,
          newDataSample: newData.slice(0, 2),
        });

        // Always update the data when hotspots change (don't compare JSON strings)
        console.log("✅ Collection data updated, refreshing collection list...");
        setCollectionData(newData);
      } catch (error) {
        console.error("Error refreshing collection data:", error);
      }
    };

    // Debounce the refresh to avoid excessive API calls
    const timeoutId = setTimeout(() => {
      refreshCollectionData();
    }, 1000); // 1 second debounce

    return () => clearTimeout(timeoutId);
  }, [hotspotContentHash, dataSourceId, project?.craftspace_id]);

  // Render the collection list
  const baseStyle: React.CSSProperties = {
    position: component.parentId ? "relative" : "absolute",
    left:
      component.position.left !== undefined
        ? typeof component.position.left === "string"
          ? component.position.left
          : `${component.position.left}px`
        : component.parentId
          ? undefined
          : "50px",
    top:
      component.position.top !== undefined
        ? typeof component.position.top === "string"
          ? component.position.top
          : `${component.position.top}px`
        : component.parentId
          ? undefined
          : "50px",
    right:
      component.position.right !== undefined
        ? typeof component.position.right === "string"
          ? component.position.right
          : `${component.position.right}px`
        : undefined,
    bottom:
      component.position.bottom !== undefined
        ? typeof component.position.bottom === "string"
          ? component.position.bottom
          : `${component.position.bottom}px`
        : undefined,
    width:
      typeof component.size.width === "number" ? `${component.size.width}px` : component.size.width,
    height:
      typeof component.size.height === "number"
        ? `${component.size.height}px`
        : component.size.height,
    border: selectedComponent?.id === component.id && !isPreviewMode ? "2px solid #0D6CFF" : "none",
    zIndex: component.zIndex,
    pointerEvents: component.pointerEvents ? "auto" : "none",
    display: "flex",
    flexDirection: component.style?.flexDirection || "column",
    gap: component.style?.gap || "8px",
    ...component.style,
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: wrapper contains interactive children
    <div
      style={baseStyle}
      onClick={(e) => onComponentClick(component, e)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onComponentClick(component, e);
        }
      }}
      role="button"
      tabIndex={0}
      data-skin-component
    >
      {isLoading ? (
        <div
          style={{
            padding: "16px",
            textAlign: "center",
            color: "#ffffff",
            fontSize: "14px",
          }}
        >
          Loading collection...
        </div>
      ) : processedData.length === 0 ? (
        <div
          style={{
            padding: "16px",
            textAlign: "center",
            color: "#ffffff",
            fontSize: "14px",
            border: "2px dashed rgba(255, 255, 255, 0.3)",
            borderRadius: "8px",
          }}
        >
          {dataSourceId ? "No items in collection" : "No collection selected"}
        </div>
      ) : (
        <>
          {/* Show all items from the collection (filtered/sorted) */}
          {processedData.map((item, index) => (
            <div
              key={getItemKey(item)}
              style={{
                padding: "8px",
                borderRadius: "4px",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                color: "#ffffff",
                fontSize: "14px",
                position: "relative",
                minHeight: "40px",
              }}
            >
              {/* If there are child components, render them with bound data */}
              {childComponents.length > 0 ? (
                renderChildComponentsForItem(item, index)
              ) : (
                <>
                  {/* Default display when no child components */}
                  {/* Display collection name prefix */}
                  {project && (
                    <span style={{ fontWeight: "bold" }}>
                      {dataSourceId === "panoramas-collection" ? "Panoramas: " : "Collection: "}
                    </span>
                  )}
                  {/* Display first field value or item name */}
                  {item.name || item[Object.keys(item)[0]] || `Item ${index + 1}`}
                </>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
};
